import React from 'react';
import classnames from 'classnames';
import { useIntl, history } from 'umi';
import { useDebounceFn } from 'ahooks';
import EllipsisHover from '@/pages/home/components/EllipsisHover';
import GameImage from '@/pages/home/components/GameImage';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { getDetailGamePathCommon } from '@/pages/intelligence/utils/topchartsUtils';
import { getGameIdByEntityGamePath } from '../../../../utils';
import { ALL_PC_DEVICES, ALL_CONSOLE_DEVICES } from '../../../../constants';
import type { IPCConsoleGame } from '@/pages/intelligence/types/pcConsoleCompare';
import './index.scss';
import ARightClickToJump from '@/pages/intelligence/components/ARightClickToJump';

export default function HeaderColumnRenderer(
  pageKey: string,
  entity: IPCConsoleGame,
  columnIndex: number,
  option: {
    devices?: string[];
  },
) {
  const { formatMessage } = useIntl();
  const getGameDetailPath = (entityType: 'pc' | 'console' | 'combine') =>{
    const gameId = getGameIdByEntityGamePath(entity, entityType);
    const path = getDetailGamePathCommon({
      entityType,
      gameId,
      tab: 'stats',
      subTab: pageKey || 'digitalRevenue',
    });
    return path;
  }

  const entityType = entity?.id_entity_type?.toLowerCase() || '';
  const hasPC = entityType?.includes('pc');
  const hasConsole = entityType?.includes('console');
  // 是否融合游戏，即存在pc、console两端的游戏
  const hasConbination = hasPC && hasConsole;
  const devices = option?.devices;
  const isPCDevices = devices?.some((device) =>
    ALL_PC_DEVICES.includes(device),
  );
  const isConsoleDevices = devices?.some((device) =>
    ALL_CONSOLE_DEVICES.includes(device),
  );
  const isConbinationDevices = isPCDevices && isConsoleDevices;

  const isPCPlus = hasPC && isPCDevices;
  const isConsolePlus = hasConsole && isConsoleDevices;
  const isConbinationPlus = hasConbination && isConbinationDevices;
  const isSelectAll = (pageKey === 'physicalRevenue' && option?.devices?.length === 7) ||
                      (pageKey === 'mau' && option?.devices?.length === 5) ||
                      (pageKey === 'digitalRevenue' && option?.devices?.length === 4)

  const { run: handleNavigate2GameDetailsPageClick } = useDebounceFn(
    (entityType: 'pc' | 'console' | 'combine', e: React.SyntheticEvent<HTMLElement>) => {
      e?.stopPropagation();
      e?.preventDefault();
      history.push(getGameDetailPath(entityType));
    },
    { wait: 300 },
  );

  return (
    <div
      className={classnames('dbi_header-column-content', {
        'is-conbination': isConbinationPlus,
      })}
    >
      <div className="dbi_header-column-content-inner">
        {(isPCPlus || isConsolePlus) && (
          <>
            <div className="dbi_game-cover-list">
              {isPCPlus && (
                <div
                  className="dbi_game-cover-item"
                  onClick={(e) => {
                    e?.persist();
                    handleNavigate2GameDetailsPageClick('pc', e);
                  }}
                >
                  <GameImage
                    className={`dbi_game-cover ${
                      isPCPlus && isConsolePlus
                        ? 'pc-cover'
                        : 'pc-console-cover'
                    } dbi-cursor`}
                    src={entity?.pcCover}
                    entityType="pc"
                    alt=""
                  />
                </div>
              )}
              {isConsolePlus && (
                <div
                  className="dbi_game-cover-item"
                  onClick={(e) => {
                    e?.persist();
                    handleNavigate2GameDetailsPageClick('console', e);
                  }}
                >
                  <GameImage
                    className="dbi_game-cover console-cover dbi-cursor"
                    src={entity?.consoleCover}
                    entityType="console"
                    alt=""
                  />
                </div>
              )}
            </div>
            <div className="dbi_game-summary">
              <ARightClickToJump
                href={getGameDetailPath(isSelectAll ? 'combine' : isPCPlus ? 'pc' : 'console')}
              >
              <div
                className="dbi_game-name dbi-cursor"
                onClick={(e) => {
                  e?.persist();
                  if(isSelectAll){
                    return handleNavigate2GameDetailsPageClick('combine', e);
                  }
                  if (isPCPlus) {
                    handleNavigate2GameDetailsPageClick('pc', e);
                    return;
                  }
                  handleNavigate2GameDetailsPageClick('console', e);
                }}
              >
                <EllipsisHover text={entity?.gameName || '-'} />
              </div>
              </ARightClickToJump>
              {isPCPlus && (
                <ARightClickToJump
                  href={getGameDetailPath('pc')}
                  className="dbi-add-entities-first-row-comp_platform-icon dbi-cursor"

                >
                <SvgIcon
                  className="dbi-add-entities-first-row-comp_platform-icon dbi-cursor"
                  type="monitor"
                  onClick={(e) => {
                    e?.persist();
                    handleNavigate2GameDetailsPageClick('pc', e);
                  }}
                />
                </ARightClickToJump>
              )}
              {isConsolePlus && (
                <ARightClickToJump
                  href={getGameDetailPath('console')}
                  className="dbi-add-entities-first-row-comp_platform-icon dbi-cursor"
                >
                <SvgIcon
                  className="dbi-add-entities-first-row-comp_platform-icon dbi-add-entities-first-row-comp_platform-console-icon dbi-cursor"
                  type="game"
                  style={{ fontSize: '13px' }}
                  onClick={(e) => {
                    e?.persist();
                    handleNavigate2GameDetailsPageClick('console', e);
                  }}
                />
                </ARightClickToJump>
              )}
            </div>
          </>
        )}
        {!isPCPlus && !isConsolePlus && isPCDevices && (
          <>
            <div
              className="dbi_game-name no-mr dbi-cursor"
              onClick={(e) => {
                e?.persist();
                handleNavigate2GameDetailsPageClick('console', e);
              }}
            >
              <EllipsisHover text={entity?.gameName || '-'} />
            </div>
            <div className="dbi_game-tips">
              {formatMessage({ id: 'dbi_compare_no_pc_version_tips' })}
            </div>
          </>
        )}
        {!isPCPlus && !isConsolePlus && isConsoleDevices && (
          <>
            <div
              className="dbi_game-name no-mr dbi-cursor"
              onClick={(e) => {
                e?.persist();
                handleNavigate2GameDetailsPageClick('pc', e);
              }}
            >
              <EllipsisHover text={entity?.gameName || '-'} />
            </div>
            <div className="dbi_game-tips">
              {formatMessage({ id: 'dbi_compare_no_console_version_tips' })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
